<template>
  <section class="admin-content" id="quality-assurance">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3">
                <i class="fe fe-check-square" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Monitoring &amp; Evaluation</h4>
                <small style="opacity: 0.8;">Facility inspection and accreditation records</small>
              </div>
            </div>
            <span v-if="accreditations.data && accreditations.data.total" class="badge badge-light" style="font-size:0.9rem; padding:8px 16px; border-radius:20px;">
              {{ accreditations.data.total }} records
            </span>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">

          <!-- Filter Card — agency users; facility users filter their own submissions by form and date -->
          <div v-if="user" class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-filter" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Filter Records</h6>
            </div>
            <div class="card-body px-4 py-4">
              <div class="form-row">

                <div class="form-group col-md-3">
                  <label class="filter-label">Select Form</label>
                  <select class="form-control" v-model="query.form_id" @change="onFormChange">
                    <option :value="null">Select form</option>
                    <option v-for="form in forms" :key="form.id" :value="form.id">{{ form.name }}</option>
                  </select>
                </div>

                <div class="form-group col-md-3" v-if="!isFacility">
                  <label class="filter-label">Select Facility <span class="text-danger">*</span></label>
                  <select class="form-control" required v-model="query.facility_id">
                    <option v-for="provider in providers" :key="provider.id" :value="provider.id">
                      {{ provider.agency_name }}
                    </option>
                  </select>
                </div>
                <div class="form-group col-md-3" v-if="!isFacility">
                  <label class="filter-label">LGA</label>
                  <select class="form-control" v-model="query.localgovt">
                    <option v-for="lga in lga_states" :key="lga.id" :value="lga.id">{{ lga.local_name }}</option>
                  </select>
                </div>
                <div class="form-group col-md-3">
                  <label class="filter-label"><i class="fe fe-calendar mr-1"></i>Start Date</label>
                  <input type="date" class="form-control" v-model="query.from" />
                </div>
                <div class="form-group col-md-3">
                  <label class="filter-label"><i class="fe fe-calendar mr-1"></i>End Date</label>
                  <input type="date" class="form-control" v-model="query.to" />
                </div>
              </div>
              <div class="d-flex flex-wrap align-items-center mt-2">
                <button class="btn btn-submit" @click="getAccreditations">
                  <i class="fe fe-search mr-1"></i> Filter Records
                </button>
                <template v-if="!isFacility">
                  <button class="btn btn-outline-success ml-2" @click="exportByLga" :disabled="!query.form_id"
                    :title="query.form_id ? 'One sheet per LGA, one row per facility, one column per question' : 'Select a form first'">
                    <i class="fe fe-download mr-1"></i> Download responses (Excel, by LGA)
                  </button>
                  <div class="custom-control custom-checkbox ml-3 d-inline-flex align-items-center">
                    <input type="checkbox" class="custom-control-input" id="qaExportLatest" v-model="exportLatestOnly" />
                    <label class="custom-control-label small" for="qaExportLatest">Latest submission per facility only</label>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- New M&E Submission — provider / provider_employee -->
          <div v-if="user && (user.type == 'provider_employee' || user.type == 'provider')" class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-plus-circle" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">New M&amp;E Submission</h6>
            </div>
            <div class="card-body px-4 py-4">
              <form @submit.prevent="makeAccreditation">
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="filter-label">Select Form <span class="text-danger">*</span></label>
                    <select class="form-control" required v-model="accreditation.form_id">
                      <option :value="item.id" v-for="item in forms" :key="item.id">{{ item.name }}</option>
                    </select>
                  </div>
                </div>

                <!-- Enrollee search — only for form 22 -->
                <div v-if="accreditation.form_id == 22" class="form-row mt-2">
                  <div class="form-group col-md-12">
                    <label class="filter-label">Search Enrollee</label>
                    <input type="text" class="form-control" placeholder="KGSHIA Number, First Name, Last Name"
                      v-model="searchkey" @change="searchIDCard" />
                    <small class="text-muted mt-1 d-block">{{ search_result.length || 0 }} enrollee(s) found</small>
                  </div>
                  <div class="form-group col-md-6" v-if="search_result && search_result.length">
                    <label class="filter-label">Select Enrollee</label>
                    <select class="form-control" v-model="selected_enrollee" required>
                      <option :value="u" v-for="u in search_result" :key="u.id">{{ u.user_query }}</option>
                    </select>
                  </div>

                  <div class="col-md-12" v-if="userDetails && selected_enrollee && selected_enrollee.id">
                    <div class="enrollee-card">
                      <div class="enrollee-photo-wrap">
                        <img :src="`https://claimify-api.hayokmedicare.ng/image/${selected_enrollee.user_image}`"
                          class="enrollee-photo" alt="Enrollee Photo"
                          onerror="this.onerror=null; this.src='/assets/img/ohis_logo.png'" />
                      </div>
                      <div class="form-row flex-grow-1">
                        <div class="form-group col-md-6">
                          <label class="filter-label">Surname</label>
                          <input type="text" class="form-control" :value="selected_enrollee.full_name" disabled />
                        </div>
                        <div class="form-group col-md-6">
                          <label class="filter-label">First Name</label>
                          <input type="text" class="form-control" :value="selected_enrollee.lastname" disabled />
                        </div>
                        <div class="form-group col-md-6">
                          <label class="filter-label">Sector</label>
                          <input type="text" class="form-control" :value="selected_enrollee.sector" disabled />
                        </div>
                        <div class="form-group col-md-6">
                          <label class="filter-label">Phone Number</label>
                          <input v-phone type="text" class="form-control" v-model="selected_enrollee.phone_number" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-3">
                  <button type="submit" class="btn btn-submit">
                    <i class="fe fe-plus mr-1"></i> Create New M&amp;E
                  </button>
                </div>
              </form>
            </div>
          </div>

          <!-- Results Table -->
          <div class="card shadow-sm table-card">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-list" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">M&amp;E Records</h6>
              <span v-if="accreditations.data && accreditations.data.total"
                class="badge badge-light ml-auto" style="border-radius:20px;">
                {{ accreditations.data.total }} total
              </span>
            </div>
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead style="background: #f8fafc;">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">Creation Date</th>
                    <th class="table-th" v-if="!isFacility">Facility Name</th>
                    <th class="table-th">Form</th>
                    <th class="table-th">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(diag, i) in accreditations.data.data" :key="diag.id" class="table-row">
                    <td class="px-4 py-3 text-muted small">{{ i + 1 }}</td>
                    <td class="py-3" style="font-size:0.85rem;">
                      {{ diag.created_at | moment("D MMM YYYY, h:mm a") }}
                    </td>
                    <td class="py-3 font-weight-bold" style="font-size:0.86rem;" v-if="!isFacility">
                      <span v-if="diag.provider">{{ diag.provider.agency_name }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="py-3" style="font-size:0.85rem;"> <span v-if="diag.form"> {{ diag.form.name }} </span> </td>
                    <td class="py-3">
                      <router-link :to="`/accreditation/${diag.id}`">
                        <button class="btn btn-sm btn-outline-info">
                          <i class="fe fe-eye mr-1"></i> View
                        </button>
                      </router-link>
                    </td>
                  </tr>
                  <tr v-if="!accreditations.data || !accreditations.data.data || !accreditations.data.data.length">
                    <td :colspan="isFacility ? 4 : 5"
                      class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size:2rem; opacity:0.3; display:block; margin-bottom:8px;"></i>
                      No records found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage" />
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import moment from "moment";
moment().format();

export default {
  components: { Navbar, Loading },
  data() {
    return {
      user: null,
      query: {
        from: moment().subtract(30, "days").format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
        form_id: null,
      },
      forms: "",
      providers: "",
      services: "",
      accreditations: { data: { data: {} } },
      showadder: false,
      isLoading: false,
      exportLatestOnly: false,
      fullPage: true,
      lga_states: "",
      accreditation: {
        name_of_facility: "",
        address: "Facility Address",
        ward: "",
        localgovt: "",
        phone_number: "",
        email: "",
        facility_type: "Public",
        accreditation_category: "Primary",
        name_of_contact_person: "",
        services: "",
      },
      searchkey: "",
      search_result: [],
      selected_enrollee: {},
      userDetails: false,
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/providerAgency/90`)
      .then((response) => { this.providers = response.data.data; })
      .catch((error) => { console.error(error); });
  },
  computed: {
    // facility users (provider / provider_employee) see their own submissions; the backend scopes the list
    isFacility() {
      return !!(this.user && ["provider", "provider_employee"].includes(this.user.type));
    },
  },
  methods: {
    onFormChange() {
      // no-op; can be used to trigger re-fetch if needed
    },
    searchIDCard() {
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/searchenrollees`, {
          agency_id: 90,
          request_query: this.searchkey,
        })
        .then((response) => {
          this.isLoading = false;
          this.search_result = response.data;
          this.userDetails = true;
          this.$toasted && this.$toasted.info("Searched Successfully", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    getAccreditations() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/qa-accreditions`, {
          user: this.user.type,
          agency_id: 90,
          facility_id: this.user.institutional_id,
          provider_id: this.query.facility_id,
          form_id: this.query.form_id,
          from: this.query.from,
          to: this.query.to,
          localgovt: this.query.localgovt,
        })
        .then((response) => {
          this.accreditations = response.data;
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    // Excel of the selected form's responses: a sheet per LGA, a row per facility submission, a column per question
    exportByLga() {
      if (!this.query.form_id) {
        this.$toasted && this.$toasted.error("Select a form first", { position: "top-center", duration: 3000 });
        return;
      }
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/qa-accreditions/export-by-lga`, {
          agency_id: this.user && this.user.type == "nhia" && this.query.selected_agency ? this.query.selected_agency.agency_id : undefined,
          form_id: this.query.form_id,
          provider_id: this.query.facility_id || undefined,
          localgovt: this.query.localgovt || undefined,
          from: this.query.from,
          to: this.query.to,
          latest_only: this.exportLatestOnly ? 1 : 0,
        }, { responseType: "blob" })
        .then((response) => {
          const form = (this.forms || []).find((f) => f.id == this.query.form_id);
          const name = ((form && form.name) || "qa-form").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${name}-responses-by-lga-${this.query.from || ""}-to-${this.query.to || ""}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.isLoading = false;
        })
        .catch(async (error) => {
          this.isLoading = false;
          let message = "Could not download the responses";
          try {
            const body = JSON.parse(await error.response.data.text());
            message = body.message || (body.errors && Object.values(body.errors)[0][0]) || message;
          } catch (e) { /* not JSON */ }
          this.$toasted && this.$toasted.error(message, { position: "top-center", duration: 4000 });
        });
    },
    getForms() {
      this.axios
        .get(`/api/v1/auth/forms/90`)
        .then((response) => { this.forms = response.data; })
        .catch((error) => { console.error(error); });
    },
    makeAccreditation() {
      this.user = JSON.parse(localStorage.getItem("user"));
      if (confirm("Are you sure you want to create a new M & E?")) {
        this.isLoading = true;
        let payload = {
          agency_id: 90,
          state: 2669,
          quality_assurance_type: "accreditation",
          accreditation_category: this.accreditation.accreditation_category,
          name_of_facility: this.user.institutional_id,
          address: this.accreditation.address,
          form_id: this.accreditation.form_id,
          ward: 0,
          localgovt: 0,
          email: this.user.email,
          services: [15],
          phone_number: this.user.phone_number,
          facility_type: this.accreditation.facility_type,
          facility_id: this.user.institutional_id,
          name_of_contact_person: this.user.full_name,
        };
        if (this.accreditation.form_id == 22 && this.selected_enrollee && this.selected_enrollee.id) {
          payload.enrollee_id = this.selected_enrollee.id;
        }
        this.axios
          .post("/api/v1/auth/quality-assurances", payload)
          .then((response) => {
            this.$router.push(`/make-accreditation-item/${response.data[0].quality_assurance_id}`);
            this.isLoading = false;
            this.$toasted.info("Fill up the form", { position: "top-center", duration: 3000 });
          })
          .catch((error) => { console.log(error.response); });
      }
    },
    fetchLga() {
      this.axios
        .get(`/api/v1/auth/lga/2669`)
        .then((response) => { this.lga_states = response.data.data; })
        .catch((error) => { console.error(error); });
    },
  },
  created() {
    this.getAccreditations();
    this.getForms();
    this.fetchLga();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  padding-bottom: 40px;
}

.header-icon-wrap {
  width: 48px; height: 48px;
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.form-card, .table-card { border-radius: 12px; border: 1px solid #e9ecef; overflow: hidden; }

.form-card-header {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.section-number {
  width: 28px; height: 28px; background: rgba(255,255,255,0.2);
  border-radius: 8px; display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
}

.filter-label {
  font-size: 0.78rem; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.04em;
  color: #64748b; margin-bottom: 4px; display: block;
}

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none; color: #fff; border-radius: 8px;
  font-weight: 600; padding: 8px 20px;
}
.btn-submit:hover { opacity: 0.9; color: #fff; }

.table-th {
  font-size: 0.73rem; text-transform: uppercase; letter-spacing: 0.04em;
  color: #64748b; font-weight: 600;
  border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}

.table-row:hover { background: #f8fafc; }

.enrollee-card {
  display: flex; gap: 20px; align-items: flex-start;
  background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 10px; padding: 16px; margin-top: 8px;
}

.enrollee-photo-wrap { flex-shrink: 0; }

.enrollee-photo {
  width: 80px; height: 80px; border-radius: 10px;
  object-fit: cover; border: 2px solid #e2e8f0;
}
</style>
